import type { FormLayout, ProjectFieldBinding, ResolvedForm } from '@servicetim/shared';
import { LoaderCircle } from 'lucide-react';
import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Controller, type UseFormReturn, useForm } from 'react-hook-form';
import { cn } from '../../lib/utils';
import { ProblemBanner } from '../feedback/problem-banner';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { applyServerErrors, mappedErrorPaths } from './errors';
import { FieldControl } from './field-control';
import {
  type BindingsOptions,
  flattenSections,
  type NormalizeExtras,
  sectionsFromBindings,
  sectionsFromResolvedForm,
} from './normalize';
import type { Pickers, SchemaFormField, SchemaFormSection, SchemaFormValues } from './types';
import {
  computeVisibility,
  type EmptyValueMode,
  isEmptyValue,
  prepareSubmitValues,
} from './values';

export interface SchemaFormSubmitContext {
  /** Keys stripped because their `visibleWhen` was false. */
  hidden: string[];
  form: UseFormReturn<SchemaFormValues>;
}

export interface SchemaFormProps {
  /** Source, exactly one of: explicit sections, a resolved form, or bindings. */
  sections?: SchemaFormSection[];
  form?: ResolvedForm;
  /** Sections for a resolved form (`Form.layout`); one section without it. */
  layout?: FormLayout;
  bindings?: readonly ProjectFieldBinding[];
  bindingOptions?: BindingsOptions;
  /** `priority_id` options (the API does not inline them into forms). */
  priorities?: NormalizeExtras['priorities'];

  defaultValues?: SchemaFormValues;
  /** Labels for id-valued defaults (user, ticket) — the ticket's `display` map. */
  display?: Record<string, string>;
  /**
   * Receives the prepared values (hidden + read-only fields stripped, empties handled per
   * `emptyValue`). Throwing an `ApiError` maps `errors[]` onto the fields automatically.
   */
  onSubmit: (values: SchemaFormValues, context: SchemaFormSubmitContext) => void | Promise<void>;
  /** Externally held error (e.g. `mutation.error`); mapped like a thrown one. */
  serverError?: unknown;
  /** `omit` (create) or `null` (patch: null clears a stored value). */
  emptyValue?: EmptyValueMode;

  submitLabel?: string;
  cancelLabel?: string;
  onCancel?: () => void;
  /** Disables the submit button (e.g. mutation pending). */
  busy?: boolean;
  /** Disables every control. */
  disabled?: boolean;
  /** Render without submit/cancel (a dialog footer submits via `form={id}`). */
  hideActions?: boolean;
  id?: string;
  className?: string;
  pickers?: Pickers;
  /** Extra content between the fields and the actions. */
  children?: ReactNode;
  /** Every change (for live previews). */
  onValuesChange?: (values: SchemaFormValues) => void;
}

/**
 * Config-driven form (ADR-0016 §3): renders sections of `FieldControl`s from a resolved form or
 * bindings, evaluates `visibleWhen` client-side, enforces `required` on visible fields, strips
 * hidden values on submit and maps server `errors[].path` back onto the fields.
 */
export function SchemaForm(props: SchemaFormProps) {
  const {
    defaultValues,
    display,
    onSubmit,
    serverError,
    emptyValue = 'omit',
    submitLabel = 'Save',
    cancelLabel = 'Cancel',
    onCancel,
    busy = false,
    disabled = false,
    hideActions = false,
    id,
    className,
    pickers,
    children,
    onValuesChange,
  } = props;

  const sections = useMemo<SchemaFormSection[]>(() => {
    if (props.sections) return props.sections;
    if (props.form)
      return sectionsFromResolvedForm(props.form, props.layout, { priorities: props.priorities });
    if (props.bindings) {
      return sectionsFromBindings(props.bindings, {
        ...props.bindingOptions,
        priorities: props.priorities,
      });
    }
    return [];
  }, [
    props.sections,
    props.form,
    props.layout,
    props.bindings,
    props.bindingOptions,
    props.priorities,
  ]);

  const knownKeys = useMemo(() => new Set(flattenSections(sections).map((f) => f.key)), [sections]);
  const form = useForm<SchemaFormValues>({
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });
  const values = form.watch();
  const { hidden } = useMemo(() => computeVisibility(sections, values), [sections, values]);
  const hiddenRef = useRef(hidden);
  hiddenRef.current = hidden;

  useEffect(() => {
    onValuesChange?.(values);
  }, [values, onValuesChange]);

  const [submitError, setSubmitError] = useState<unknown>(null);
  const bannerError = submitError ?? serverError ?? null;
  useEffect(() => {
    if (serverError) applyServerErrors(serverError, form.setError, knownKeys);
  }, [serverError, form.setError, knownKeys]);
  const omitPaths = useMemo(
    () => mappedErrorPaths(bannerError, knownKeys),
    [bannerError, knownKeys],
  );

  const formId = useId();
  const submit = form.handleSubmit(async (raw) => {
    setSubmitError(null);
    const prepared = prepareSubmitValues(sections, raw, { emptyValue });
    try {
      await onSubmit(prepared.values, { hidden: prepared.hidden, form });
    } catch (error) {
      setSubmitError(error);
      applyServerErrors(error, form.setError, knownKeys);
    }
  });

  return (
    <form
      id={id ?? formId}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        void submit(event);
      }}
      className={cn('space-y-6', className)}
      data-slot="schema-form"
    >
      {bannerError ? <ProblemBanner error={bannerError} omitPaths={omitPaths} size="sm" /> : null}
      {sections.map((section, index) => {
        const visibleFields = section.fields.filter((f) => !hidden.has(f.key));
        if (visibleFields.length === 0) return null;
        return (
          <fieldset key={section.title ?? index} className="space-y-4">
            {section.title || section.description ? (
              <div className="space-y-1">
                {section.title ? (
                  <legend className="font-semibold text-fg">{section.title}</legend>
                ) : null}
                {section.description ? (
                  <p className="text-fg-muted text-sm">{section.description}</p>
                ) : null}
              </div>
            ) : null}
            {visibleFields.map((field) => (
              <Controller
                key={field.key}
                name={field.key}
                control={form.control}
                rules={{
                  validate: (value) =>
                    hiddenRef.current.has(field.key) ||
                    field.readOnly ||
                    !field.required ||
                    !isEmptyValue(value) ||
                    `${field.label} is required`,
                }}
                render={({ field: rhf, fieldState }) => (
                  <FieldRow
                    field={field}
                    error={fieldState.error?.message}
                    inputId={`${formId}-${field.key}`}
                  >
                    {(inputId, describedBy) => (
                      <FieldControl
                        field={field}
                        value={rhf.value}
                        onChange={(next) => rhf.onChange(next)}
                        onBlur={rhf.onBlur}
                        id={inputId}
                        disabled={disabled}
                        invalid={fieldState.invalid}
                        describedBy={describedBy}
                        displayLabel={display?.[field.key]}
                        pickers={pickers}
                      />
                    )}
                  </FieldRow>
                )}
              />
            ))}
          </fieldset>
        );
      })}
      {children}
      {hideActions ? null : (
        <div className="flex items-center justify-end gap-2">
          {onCancel ? (
            <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>
              {cancelLabel}
            </Button>
          ) : null}
          <Button type="submit" disabled={busy || disabled || form.formState.isSubmitting}>
            {busy || form.formState.isSubmitting ? <LoaderCircle className="animate-spin" /> : null}
            {submitLabel}
          </Button>
        </div>
      )}
    </form>
  );
}

interface FieldRowProps {
  field: SchemaFormField;
  error?: string;
  inputId: string;
  children: (inputId: string, describedBy: string | undefined) => ReactNode;
}

/** Label + control + description + error, with the aria wiring. Booleans put the label after. */
export function FieldRow({ field, error, inputId, children }: FieldRowProps) {
  const descriptionId = field.description ? `${inputId}-description` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy = [descriptionId, errorId].filter(Boolean).join(' ') || undefined;
  const label = (
    <Label htmlFor={inputId}>
      {field.label}
      {field.required ? (
        <span aria-hidden="true" className="text-danger">
          *
        </span>
      ) : null}
      {field.readOnly ? (
        <span className="font-normal text-fg-muted text-xs">(read-only)</span>
      ) : null}
    </Label>
  );
  return (
    <div className="space-y-1.5" data-field-key={field.key} data-field-type={field.type}>
      {field.type === 'boolean' ? (
        <div className="flex items-center gap-2">
          {children(inputId, describedBy)}
          {label}
        </div>
      ) : (
        <>
          {label}
          {children(inputId, describedBy)}
        </>
      )}
      {field.description ? (
        <p id={descriptionId} className="text-fg-muted text-xs">
          {field.description}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} role="alert" className="text-danger text-xs">
          {error}
        </p>
      ) : null}
    </div>
  );
}
